import React, { useEffect, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { View, Image, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faMapLocation,
  faDollarSign,
  faExclamationCircle,
  faPlus,
  faMinus,
  faArrowRightLong,
  faRemove,
} from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'react-native-toast-notifications';
import { Page, BText, Drawer, showToast } from '../../components';
import { RegistrationTickets } from '../../components/events';
import { Button } from '../../components/Form';
import { formatTime24 } from '../../utils/dateUtils';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { EventModel, MyEventModel } from '../../types';
import { RegisteredSuccess } from '../../components/events';
import { setLoading } from '../../store/portal/portalActions';
import { fetchAllEvents } from '../../store/events/eventActions';
import { breakpointWidth } from '../../utils/helper';
import { themeConfig } from '../../styles/themeConfig';
import { validateLogon } from '../../utils/auth';
import { registerMyEvents, clearMyEventsRegStatus } from '../../store/myEvents/myEventActions';
import attachmentService from '../../services/attachmentService';
import { LoginForm } from '../../components/Auth';

export const CheckoutEvent = ({ navigation }: any) => {
  const toast = useToast();
  const route = useRoute();

  const dispatch = useAppDispatch();

  const [rtPortal, rtUser, rtEvents, rtMyEvents] = useAppSelector(s => [
    s.portal,
    s.user,
    s.event,
    s.myEvent,
    s.myFavorites,
  ]);

  const { eventId } = route.params as { eventId: string };
  const [event, setEvent] = useState<EventModel>();
  const [registeredEvent, setRegisteredEvent] = useState<MyEventModel | null>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  const [isLogon, setLogon] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);

  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [showRegistered, setRegistered] = useState<boolean>(false);
  const [showSuccess, setSuccess] = useState<boolean>(false);
  const [showTicket, setShowTicket] = useState<boolean>(false);

  const serviceCharge = 5;
  const [ticketQuantity, setTicketQuantity] = useState<number>(1);
  const [ticketSubTotal, setTicketSubTotal] = useState<number>(50);
  const [ticketGrandTotal, setTicketGrandTotal] = useState<number>(ticketSubTotal * ticketQuantity + serviceCharge);
  const { voucherName } = route.params as { voucherName: string };
  const { voucherAmount } = route.params as { voucherAmount: number };
  const [voucherApplied, setVoucherApplied] = useState<boolean>(false);

  useEffect(() => {
    // set voucher applied to true if have voucherName & voucherAmount
    if (voucherAmount > 0) {
      setVoucherApplied(true);
      // totalTicketPrice()
    }
  }, [voucherAmount]);

  useEffect(() => {
    // Reset Reg Status
    dispatch(clearMyEventsRegStatus());
  }, []);

  useEffect(() => {
    (async () => {
      if (await validateLogon(rtUser)) {
        // setLogon(true);
        console.log('is login', true);
      }
    })();
  }, [rtUser.user]);

  useEffect(() => {
    const events: EventModel[] = rtEvents.allEvents;
    let curEvent: EventModel | undefined = events.find((a: EventModel) => a._id === eventId);

    if (!rtEvents.loaded || !curEvent) {
      console.log('event not loaded, fetch by ID');
      dispatch(setLoading(true, 'Checkout'));
      dispatch(
        fetchAllEvents({
          limit: 30,
          searchParam: {
            eventId: eventId,
          },
          countToGet: 5,
        }),
      );
    } else {
      dispatch(setLoading(false, 'Checkout'));
      setEvent(curEvent);

      getImage();
    }
  }, [rtEvents, eventId]);

  // to handle is the event is registered
  useEffect(() => {
    if (rtMyEvents.allEvents.length > 0 && eventId) {
      let myEvent: MyEventModel | null | undefined = rtMyEvents.allEvents.find(
        (e: MyEventModel) => e.eventId === eventId,
      );

      setRegisteredEvent(myEvent || null);

      dispatch(setLoading(false, 'Checkout'));

      if (myEvent) {
        setRegistered(true);
      } else {
        setRegistered(false);
      }
    }
  }, [rtMyEvents, eventId]);

  // to handle registration success & show success message
  useEffect(() => {
    setSuccess(rtMyEvents.status);
  }, [rtMyEvents.status]);

  useEffect(() => {
    (async () => {
      if (await validateLogon(rtUser)) {
        setLogon(true);
        console.log('is login', true);
      }
    })();
  }, [rtUser.user]);

  useEffect(() => {
    totalTicketPrice();
  }, [ticketQuantity, ticketSubTotal, ticketGrandTotal, serviceCharge, voucherAmount, voucherApplied]);

  const proceedToPay = () => {
    if (isLogon) {
      if (event) {
        showToast(toast, { title: 'Future', message: 'Proceed to payment screen!!' });
      }
    } else {
      setShowLogin(true);
    }
  };

  const confirmRegistration = () => {
    if (event) {
      dispatch(setLoading(true, 'Checkout - confirm reg'));
      dispatch(registerMyEvents(event));
    }
  };

  const getImage = async () => {
    const attachment = await attachmentService.getAttachment('checkout', eventId);
    setThumbnail(attachment.data.files[0].image);
  };

  const handleAdd = () => {
    setTicketQuantity(ticketQuantity + 1);
    totalTicketPrice();
  };

  const handleSubtract = () => {
    if (ticketQuantity > 1) {
      setTicketQuantity(ticketQuantity - 1);
      totalTicketPrice();
    }
  };

  const totalTicketPrice = () => {
    if (event) {
      setTicketSubTotal(event?.eventPrice * ticketQuantity);
      setTicketGrandTotal(
        voucherApplied ? ticketSubTotal - voucherAmount + serviceCharge : ticketSubTotal + serviceCharge,
      );
    }
  };

  const handleRemoveVoucher = () => {
    if (voucherAmount) {
      setVoucherApplied(false);
      totalTicketPrice();
    }
  };

  const handleNavigate = () => {
    navigation.navigate('voucher', { eventId });
  };

  return (
    <Page
      title={''}
      style={[styles.container, showRegistered ? styles.registeredBg : null]}
      contentStyle={[
        rtPortal.isSmallScreen
          ? { height: rtPortal.height - themeConfig.settings.mobileContentOffsetWithoutPageTitle, paddingBottom: 36 }
          : null,
        showRegistered ? styles.registeredBg : null,
      ]}
      useScrollView={!rtPortal.isSmallScreen}
    >
      {event && (
        <ScrollView>
          <View
            style={[
              styles.verticalFlex,
              rtPortal.isSmallScreen
                ? { width: '100%', flexDirection: 'column' }
                : { width: breakpointWidth(rtPortal.breakpoint) },
            ]}
          >
            <View style={[styles.card, rtPortal.isSmallScreen ? styles.cardSM : styles.cardMD]}>
              {thumbnail && (
                <View style={!rtPortal.isSmallScreen ? styles.thumbMDContainer : styles.thumbSMContainer}>
                  <Image source={{ uri: thumbnail }} style={styles.thumbImg} />
                </View>
              )}
              <BText style={styles.cardTitle}>Task Details</BText>
              {/* Event Type */}
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faExclamationCircle} size="1x" />
                </View>
                <BText style={styles.cardItemText}>Conference</BText>
              </View>
              {/* Event Organizer */}
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faExclamationCircle} size="1x" />
                </View>
                <BText style={styles.cardItemText}>{event.organizer.company}</BText>
              </View>
              {/* Event Venue */}
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faMapLocation} size="1x" />
                </View>
                <BText style={styles.cardItemText}>
                  Hosted at {event.venueInformation.address} . {event.venueInformation.city}
                </BText>
              </View>
              {/* Event Date */}
              <View style={styles.cardItemContainer}>
                <View style={styles.cardItemIconContainer}>
                  <FontAwesomeIcon icon={faCalendar} size="1x" />
                </View>
                <BText style={styles.cardItemText}>
                  {event.displayDate}
                  {'\n'}
                  {formatTime24(event.eventTime.start || '')} - {formatTime24(event.eventTime.end || '')}
                </BText>
              </View>
              {/* Event Price */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <View style={styles.cardItemContainer}>
                  <BText style={styles.cardItemText}>RM {event.eventPrice}</BText>
                </View>
                <View style={styles.cardMinusAddContainer}>
                  <TouchableOpacity onPress={handleSubtract} style={styles.cardMinusPlusIcon}>
                    <FontAwesomeIcon icon={faMinus} size="1x" />
                  </TouchableOpacity>
                  <View>
                    <BText style={styles.cardTicketQuantity}> {ticketQuantity} </BText>
                  </View>
                  <TouchableOpacity onPress={handleAdd} style={styles.cardMinusPlusIcon}>
                    <FontAwesomeIcon icon={faPlus} size="1x" />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      )}
      {event && (
        <View style={styles.btnRegOrViewTicketContainerSM}>
          <View style={styles.ticketCalculationContainer}>
            <View style={styles.ticketSubCalculationContainer}>
              <BText style={styles.ticketCalculationText}>Subtotal</BText>
              <BText style={styles.ticketCalculationText}>RM {ticketSubTotal}</BText>
            </View>
            <View style={styles.ticketSubCalculationContainer}>
              <BText style={styles.ticketCalculationText}>Serive Charge</BText>
              <BText style={styles.ticketCalculationText}>RM {serviceCharge}</BText>
            </View>
            <View style={styles.divider} />

            {!voucherApplied ? (
              <TouchableOpacity onPress={() => handleNavigate()} style={styles.ticketSubCalculationContainer}>
                <View style={{ flex: 1, flexDirection: 'row' }}>
                  <FontAwesomeIcon icon={faDollarSign} size="1x" color="green" />
                  <BText style={{ fontSize: 13, fontWeight: 'bold', paddingLeft: 10 }}>
                    Apply Promo to get discount
                  </BText>
                </View>
                <FontAwesomeIcon icon={faArrowRightLong} size="1x" />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={styles.ticketSubCalculationContainer} onPress={() => handleRemoveVoucher()}>
                <View style={{ flex: 1, flexDirection: 'row' }}>
                  <FontAwesomeIcon icon={faRemove} size="1x" color="red" />
                  <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}>
                    <BText style={{ fontSize: 13, fontWeight: 'bold', paddingLeft: 10 }}>{voucherName}</BText>
                    <BText style={{ fontSize: 13, fontWeight: 'bold', paddingLeft: 10 }}>- RM {voucherAmount}</BText>
                  </View>
                </View>
              </TouchableOpacity>
            )}

            <View style={styles.divider} />
            <View style={styles.ticketSubCalculationContainer}>
              <BText style={styles.ticketCalculationText}>Grand Total</BText>
              <BText style={styles.ticketCalculationText}>RM {ticketGrandTotal}</BText>
            </View>
          </View>
          <Button onPress={proceedToPay}>Pay</Button>
        </View>
      )}

      <Drawer
        id="event.registration"
        visible={showRegistration}
        type={'onCancel'}
        onClose={() => setShowRegistration(false)}
        onOK={confirmRegistration}
        title={'Confirmation'}
      >
        <BText>Are you confirm the booking?</BText>
      </Drawer>

      <Drawer id="checkout.registered" visible={showSuccess} type={'custom'} onClose={() => setSuccess(false)}>
        {registeredEvent && <RegisteredSuccess event={registeredEvent} onClose={() => setSuccess(false)} />}
      </Drawer>

      <Drawer id="evecheckoutnt.ticket" visible={showTicket} onClose={() => setShowTicket(false)} type="custom">
        {registeredEvent && <RegistrationTickets event={registeredEvent} onClose={() => setShowTicket(false)} />}
      </Drawer>

      <Drawer id="checkout.login" visible={showLogin} type="custom" onClose={() => setShowLogin(false)}>
        <LoginForm onClose={() => setShowLogin(false)} navigation={navigation} />
      </Drawer>
    </Page>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: themeConfig.colors.background,
    position: 'relative',
  },
  registeredBg: {
    backgroundColor: themeConfig.colors.backgroundRegistered,
  },
  verticalFlex: {
    flexDirection: 'column',
    marginHorizontal: 'auto',
  },
  eventDetails: {
    //paddingTop: 32,
    marginVertical: 0,
    marginHorizontal: 'auto',
  },
  registerTagMD: {},
  registerTagSM: {
    marginTop: 16,
  },
  thumbMDContainer: {
    flex: 4,
    maxWidth: 400,
  },
  thumbSMContainer: {
    width: '100%',
  },
  thumbImg: {
    width: '100%',
    height: undefined,
    aspectRatio: 4 / 3,
  },
  thumbSMImg: {
    width: '100%',
    height: '100%',
    aspectRatio: 4 / 3,
  },
  detailMDContainer: {
    flex: 6,
  },
  paddingHorizontal16: {
    paddingHorizontal: 16,
  },
  title: {
    flexDirection: 'column',
    paddingTop: 24,
    paddingBottom: 8,
    fontSize: 24,
    fontWeight: 'bold',
  },
  desc: {
    paddingVertical: 0,
  },
  viewMoreToggle: {
    paddingVertical: 5,
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  btnContainer: {
    paddingVertical: 20,
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  btnItemContainer: {
    width: 40,
    flex: -1,
  },
  btnItem: {
    height: 32,
    width: 32,
    borderRadius: 16,
    flex: 1,
  },
  favIcon: {
    color: themeConfig.colors.white,
  },
  favIconSelected: {
    color: themeConfig.colors.favColor,
  },
  btnRegOrViewTicketContainerMD: {
    paddingVertical: 0,
    paddingHorizontal: 16,
    width: 150,
    flex: 3,
  },
  btnRegOrViewTicketContainerSM: {
    paddingVertical: 0,
    // paddingHorizontal: 16,
    position: 'absolute',
    bottom: 0,
    width: '100%',
  },
  card: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    backgroundColor: themeConfig.colors.white,
    borderRadius: themeConfig.settings.borderRadius,
    marginBottom: 10,
  },
  cardMD: {
    marginHorizontal: 0,
    marginTop: 16,
  },
  cardSM: {
    marginTop: 0,
    marginBottom: '50%',
  },
  cardTitle: {
    fontSize: 21,
    fontWeight: '500',
  },
  cardItemContainer: {
    paddingTop: 5,
    flexDirection: 'row',
  },
  cardMinusAddContainer: {
    paddingTop: 5,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  cardItemIconContainer: {
    textVerticalAlign: 'top',
    width: 30,
    paddingTop: 5,
    paddingRight: 14,
  },
  cardItemIcon: {
    // ratio
    width: '12',
    // Without height undefined it won't work
    height: undefined,
    // figure out your image aspect ratio
    aspectRatio: 14 / 14,
  },
  cardItemText: {
    fontSize: 14,
    color: themeConfig.colors.black,
  },
  cardSpeakersContainer: {
    paddingTop: 16,
    flexDirection: 'column',
  },
  cardTicketQuantity: {
    paddingStart: 5,
    paddingEnd: 5,
    fontSize: 16,
    color: themeConfig.colors.black,
    fontWeight: 'bold',
  },
  cardMinusPlusIcon: {
    flexDirection: 'row',
    color: themeConfig.colors.black,
  },
  divider: {
    width: '100%',
    height: 0.8,
    backgroundColor: '#000',
    marginTop: 8,
    marginBottom: 8,
  },
  ticketCalculationContainer: {
    flex: 1,
    backgroundColor: '#fff',
    padding: 10,
    marginBottom: 10,
  },
  ticketSubCalculationContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  ticketCalculationText: {
    fontSize: 15,
    fontWeight: 'bold',
  },
});
